import { Injectable } from '@angular/core';
import { InspectionFactoryService } from '../../shared/services/inspectionFactory.service';
import { IndexedDbService } from '../../shared/services/indexedDb.service';
import { forkJoin } from 'rxjs';
import { flatMap, map, take } from 'rxjs/operators';
import { Observable } from '../../../../node_modules/rxjs';
import { DataService } from '../../shared/services';
import { InspectionPlugin } from '../../shared/models';

@Injectable()
export class PartsPluginFactoryService extends InspectionFactoryService {
  constructor(indexedDb: IndexedDbService, dataService: DataService) {
    super(indexedDb, dataService);
  }

  public addParts(inspectionId: string, pointId: string, pluginId: string, parts: any[]): Observable<any> {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        flatMap((plugin) => {
          if (plugin) {
            return this.dataService.getPartsPlugin(inspectionId).pipe(
              map((partsPlugin) => {
                if (partsPlugin) {
                  this._addParts(partsPlugin, pointId, parts);
                  return partsPlugin;
                }
              })
            );
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const partsPlugin = inspection.allPlugins.find((p) => p.pluginType === 'parts');
        if (!partsPlugin || !parts) {
          return null;
        }
        this._addParts(partsPlugin, pointId, parts);
        partsPlugin.etag = new Date();
        this.updateInspection(inspection);
        return partsPlugin;
      })
    );
  }

  private _addParts(partsPlugin: InspectionPlugin, pointId: string, parts: any[]) {
    const config = partsPlugin.config;
    if (!config.Basket) {
      config.Basket = {};
    }
    if (!config.Basket.PartsView) {
      config.Basket.PartsView = [];
    }

    parts.forEach((p) => (p.PointId = pointId));
    const partsToAdd = parts.filter((part) => {
      return !config.Basket.PartsView.find(
        (p) =>
          `${p.Parts}${p.Page}${p.Position}${p.Name}`.toLowerCase() ===
          `${part.Id}${part.Page}${part.Position}${part.Name}`.toLowerCase()
      );
    });
    config.Basket.PartsView.push(
      ...partsToAdd.map((part) => {
        return {
          Parts: part.Id,
          Page: part.Page,
          Position: part.Position,
          Quantity: 0,
          Name: part.Name,
          Machine: part.EquipmentId
        };
      })
    );

    parts.forEach((part) => {
      const addedPart = config.Parts.find(
        (p) =>
          `${p.Id}${p.Page}${p.Position}${p.Name}${p.PointId}`.toLowerCase() ===
          `${part.Id}${part.Page}${part.Position}${part.Name}${part.PointId}`.toLowerCase()
      );
      if (!addedPart && part.Quantity > 0) {
        config.Parts.push(part);
      } else {
        addedPart.Quantity += part.Quantity;
      }
    });
    config.Parts = config.Parts.filter((p) => p.PointId !== '-1');
  }

  public updateParts(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    parts: any[],
    pointParts: any
  ): Observable<InspectionPlugin> {
    if (this.isManager) {
      return this.dataService.getInspectionPoints(inspectionId).pipe(
        take(1),
        flatMap((points) => {
          return this.dataService.getPartsPlugin(inspectionId).pipe(
            map((partsPlugin) => {
              if (partsPlugin) {
                this._updateParts(
                  partsPlugin,
                  pointId,
                  parts,
                  points.map((p) => p.id),
                  pointParts
                );
                return partsPlugin;
              }
            })
          );
        })
      );
    }
    return forkJoin({
      inspection: this.getInspection(inspectionId).pipe(take(1)),
      points: this.dataService.getInspectionPoints(inspectionId).pipe(take(1))
    }).pipe(
      map((res) => {
        const partsPlugin = res.inspection.allPlugins.find((p) => p.pluginType === 'parts');
        if (!partsPlugin || !parts) {
          return null;
        }
        this._updateParts(
          partsPlugin,
          pointId,
          parts,
          res.points.map((p) => p.id),
          pointParts
        );
        this.updateInspection(res.inspection);
        return partsPlugin;
      })
    );
  }

  private _updateParts(
    partsPlugin: InspectionPlugin,
    pointId: string,
    parts: any[],
    allPoints: string[],
    pointParts: any
  ) {
    const config = partsPlugin.config;
    if (pointId === '-1') {
      parts.forEach((p) => {
        const partView = config.Basket.PartsView.find((part) => part.Parts === p.Id);
        if (partView) {
          partView.Quotation = p.Quotation;
          partView.Currency = p.Currency;
        }

        config.Parts.filter((part) => p.Id === part.Id).forEach((part) => {
          part.Quotation = p.Quotation;
          part.Currency = p.Currency;
        });
      });
    } else {
      config.Parts = config.Parts.filter((part) => part.PointId !== pointId);
      config.Parts.push(...parts);
      const allParts = config.Parts.filter((p) => allPoints.includes(p.PointId));
      config.Basket.PartsView.forEach((part) => {
        const parts = allParts.filter((p) => p.Id === part.Parts && p.PointId !== '-1');
        const totalQuantity = parts.reduce((a, b) => a + b.Quantity, 0);
        part.Total = totalQuantity;
        parts.forEach((p) => (p.Total = totalQuantity));
      });
    }

    config.Parts = config.Parts.filter((p) => p.PointId !== '-1');

    if (!pointParts) return;

    if (!config.PointParts) {
      config.PointParts = {};
    }
    if (config.PointParts[pointParts.PointId]) {
      config.PointParts[pointParts.PointId].NeedParts = pointParts.NeedParts;
      config.PointParts[pointParts.PointId].Comment = pointParts.Comment;
    } else {
      config.PointParts[pointParts.PointId] = pointParts;
    }
  }

  public updatePart(inspectionId: string, pointId: string, pluginId: string, oldPart: any, part: any): Observable<any> {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        flatMap((plugin) => {
          if (plugin) {
            return this.dataService.getPartsPlugin(inspectionId).pipe(
              map((partsPlugin) => {
                if (partsPlugin) {
                  this._updatePart(partsPlugin, oldPart, part);
                  return partsPlugin;
                }
              })
            );
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const partsPlugin = inspection.allPlugins.find((p) => p.pluginType === 'parts');
        if (!partsPlugin || !oldPart || !part) {
          return null;
        }
        this._updatePart(partsPlugin, oldPart, part);
        partsPlugin.etag = new Date();
        this.updateInspection(inspection);
        return partsPlugin;
      })
    );
  }

  private _updatePart(partsPlugin: InspectionPlugin, oldPart: any, part: any) {
    // we assume that all parts in the list have different ids

    const config = partsPlugin.config;

    // in config
    // Basket.PartsView is the list of all parts with qty = 0
    // PointParts is the plugin config for each point, without the parts
    // Parts is the list of parts with qty for each point. It contains only non null quantity

    const replaceIndex = config.Basket.PartsView.findIndex(
      (p) =>
        `${p.Parts}${p.Page}${p.Position}${p.Name}`.toLowerCase() ===
        `${oldPart.Id}${oldPart.Page}${oldPart.Position}${oldPart.Name}`.toLowerCase()
    );
    const newBasketPart = {
      Parts: part.Id,
      Page: part.Page,
      Position: part.Position,
      Quantity: 0,
      Name: part.Name,
      Machine: ''
    };
    if (replaceIndex >= 0) {
      config.Basket.PartsView[replaceIndex] = newBasketPart;
    } else {
      config.Basket.PartsView.push(newBasketPart);
    }

    if (oldPart.Quantity > 0) {
      // Note : if oldPart.Quantity == 0, the part is not present in config.Parts
      const oldId = `${oldPart.Id}${oldPart.Page}${oldPart.Position}${oldPart.Name}${oldPart.PointId}`.toLowerCase();
      const newId = `${part.Id}${part.Page}${part.Position}${part.Name}${part.PointId}`.toLowerCase();
      const existingOldIndex = config.Parts.findIndex(
        (p) => `${p.Id}${p.Page}${p.Position}${p.Name}${p.PointId}`.toLowerCase() === oldId
      );
      const existingNewPart = config.Parts.find(
        (p) => `${p.Id}${p.Page}${p.Position}${p.Name}${p.PointId}`.toLowerCase() === newId
      );

      if (oldId !== newId && existingNewPart) {
        // new edited part has the same name of an already existing part. we have to merge them :

        // historical : remove all parts with old id
        // we could also do this : if (existingOldIndex >= 0) config.Parts.splice(existingOldIndex, 1)
        config.Parts = config.Parts.filter(
          (p) => `${p.Id}${p.Page}${p.Position}${p.Name}${p.PointId}`.toLowerCase() !== oldId
        );
        existingNewPart.Quantity += part.Quantity;
      } else {
        if (existingOldIndex >= 0) {
          config.Parts[existingOldIndex] = part;
        } else {
          console.error('BUG ! part with quantity > 0 not found', oldId);
          config.Parts.push(part);
        }
      }
    }
  }

  public deleteParts(inspectionId: string, pointId: string, pluginId: string): Observable<any> {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        flatMap((plugin) => {
          if (plugin) {
            return this.dataService.getPartsPlugin(inspectionId).pipe(
              map((partsPlugin) => {
                if (partsPlugin) {
                  this._deletePointParts(partsPlugin, pointId);
                  return partsPlugin;
                }
              })
            );
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const partsPlugin = inspection.allPlugins.find((p) => p.pluginType === 'parts');
        if (!partsPlugin) {
          return null;
        }
        this._deletePointParts(partsPlugin, pointId);
        partsPlugin.etag = new Date();
        this.updateInspection(inspection);
        return partsPlugin;
      })
    );
  }

  private _deletePointParts(partsPlugin: InspectionPlugin, pointId: string) {
    const config = partsPlugin.config;
    config.Parts = config.Parts.filter((p) => p.PointId !== pointId);
    if (!config.PointParts) {
      config.PointParts = {};
    }
    if (config.PointParts[pointId]) {
      config.PointParts[pointId] = {};
    }
    if (config.PointParts['-1']) {
      config.PointParts['-1'] = null;
    }
  }

  public deletePart(inspectionId: string, part: any): Observable<any> {
    if (this.isManager) {
      return this.dataService.getPartsPlugin(inspectionId).pipe(
        map((partsPlugin) => {
          if (partsPlugin) {
            this._deletePart(partsPlugin, part);
            return partsPlugin;
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const partsPlugin = inspection.allPlugins.find((p) => p.pluginType === 'parts');
        if (!partsPlugin) {
          return null;
        }
        this._deletePart(partsPlugin, part);
        partsPlugin.etag = new Date();
        this.updateInspection(inspection);
        return partsPlugin;
      })
    );
  }

  private _deletePart(partsPlugin: InspectionPlugin, part: any) {
    const config = partsPlugin.config;
    config.Basket.PartsView = config.Basket.PartsView.filter(
      (p) =>
        `${p.Parts}${p.Page}${p.Position}${p.Name}`.toLowerCase() !==
        `${part.Id}${part.Page}${part.Position}${part.Name}`.toLowerCase()
    );

    config.Parts = config.Parts.filter(
      (p) =>
        `${p.Id}${p.Page}${p.Position}${p.Name}${p.PointId}`.toLowerCase() !==
        `${part.Id}${part.Page}${part.Position}${part.Name}${part.PointId}`.toLowerCase()
    );
  }
}
